import { EnvelopeDirection } from '@const/consts';
import { IEnvelopeField } from '@store/modules/boxes/types';
import {
  EnvelopeCardSummaryLayout,
  EnvelopeViewMode,
  IUserEnvelopeFieldsViewSettings,
  IUserEnvelopeFavoriteViewSettings,
  IUserEnvelopeFiltersViewSettings,
  IUserEnvelopeViewSettings,
} from '@store/modules/user/types';

import { getEnvelopeListFields } from './dynamicFields';

export const ENVELOPE_LIST_PREVIEW_LIMIT = 3;
export const DEFAULT_ENVELOPE_VIEW_MODE = EnvelopeViewMode.LIST;
export const DEFAULT_ENVELOPE_CARD_SUMMARY_LAYOUT = EnvelopeCardSummaryLayout.TOP;

const getValidFieldKeys = (fields: IEnvelopeField[]): string[] =>
  fields.map(({ systemKey }) => systemKey);

export const getEnvelopeViewMode = (
  settings?: IUserEnvelopeViewSettings,
  favoriteFilterOguid?: string | null,
  boxOguid?: string
): EnvelopeViewMode => {
  const baseViewMode = boxOguid && settings?.viewModesByBox
    ? settings.viewModesByBox[boxOguid]
    : settings?.viewMode;
  const viewMode = favoriteFilterOguid
    ? settings?.favoriteViewsByFilter?.[favoriteFilterOguid]?.viewMode ?? baseViewMode
    : baseViewMode;

  return viewMode === EnvelopeViewMode.TABLE
    ? EnvelopeViewMode.TABLE
    : DEFAULT_ENVELOPE_VIEW_MODE;
};

export const getEnvelopeCardSummaryLayout = (
  settings?: IUserEnvelopeViewSettings
): EnvelopeCardSummaryLayout =>
  settings?.cardSummaryLayout === EnvelopeCardSummaryLayout.LEFT
    ? EnvelopeCardSummaryLayout.LEFT
    : DEFAULT_ENVELOPE_CARD_SUMMARY_LAYOUT;

export const getBoxFieldsViewSettings = (
  settings: IUserEnvelopeViewSettings | undefined,
  boxOguid: string,
  favoriteFilterOguid?: string | null
): IUserEnvelopeFieldsViewSettings => (
  (favoriteFilterOguid
    ? settings?.favoriteViewsByFilter?.[favoriteFilterOguid]?.fields ?? settings?.fieldsByBox?.[boxOguid]
    : settings?.fieldsByBox?.[boxOguid]) ?? {}
);

export const getBoxFiltersViewSettings = (
  settings: IUserEnvelopeViewSettings | undefined,
  boxOguid: string,
  favoriteFilterOguid?: string | null
): IUserEnvelopeFiltersViewSettings => (
  (favoriteFilterOguid
    ? settings?.favoriteViewsByFilter?.[favoriteFilterOguid]?.filters ?? settings?.filtersByBox?.[boxOguid]
    : settings?.filtersByBox?.[boxOguid]) ?? {}
);

export const getOrderedFieldKeys = (
  fields: IEnvelopeField[],
  order?: string[]
): string[] => {
  const fieldKeys = getValidFieldKeys(fields);
  const fieldKeysSet = new Set(fieldKeys);
  const orderedKeys = (order ?? []).filter((key) => fieldKeysSet.has(key));
  const orderedKeysSet = new Set(orderedKeys);

  return [
    ...orderedKeys,
    ...fieldKeys.filter((key) => !orderedKeysSet.has(key)),
  ];
};

export const getEnvelopeFieldsViewSettingsWithDefaults = (
  fields: IEnvelopeField[],
  settings?: IUserEnvelopeFieldsViewSettings
): Required<IUserEnvelopeFieldsViewSettings> => {
  const order = getOrderedFieldKeys(fields, settings?.order);
  const fieldKeysSet = new Set(order);
  const hasSavedSettings = Boolean(settings?.order || settings?.hidden);
  const hidden = hasSavedSettings
    ? (settings?.hidden ?? []).filter((fieldKey) => fieldKeysSet.has(fieldKey))
    : [];

  return {
    hidden,
    order,
  };
};

export const getOrderedEnvelopeFields = (
  fields: IEnvelopeField[],
  order?: string[]
): IEnvelopeField[] => {
  const fieldsByKey = fields.reduce<Record<string, IEnvelopeField>>((acc, field) => {
    acc[field.systemKey] = field;

    return acc;
  }, {});

  return getOrderedFieldKeys(fields, order)
    .map((fieldKey) => fieldsByKey[fieldKey])
    .filter((field): field is IEnvelopeField => Boolean(field));
};

export const getVisibleEnvelopeFields = (
  fields: IEnvelopeField[],
  settings?: IUserEnvelopeFieldsViewSettings
): IEnvelopeField[] => {
  const fieldsSettings = getEnvelopeFieldsViewSettingsWithDefaults(fields, settings);
  const hiddenKeys = new Set(fieldsSettings.hidden);

  return getOrderedEnvelopeFields(fields, fieldsSettings.order)
    .filter((field) => !hiddenKeys.has(field.systemKey));
};

export const getEnvelopeViewFields = (
  fields: IEnvelopeField[],
  direction: EnvelopeDirection | null | undefined,
  boxOguid: string,
  settings?: IUserEnvelopeViewSettings,
  favoriteFilterOguid?: string | null
): IEnvelopeField[] =>
  getVisibleEnvelopeFields(
    getEnvelopeListFields(fields, direction),
    getBoxFieldsViewSettings(settings, boxOguid, favoriteFilterOguid)
  );

const getFavoriteViewSettingsNextValue = (
  currentSettings: IUserEnvelopeViewSettings | undefined,
  favoriteFilterOguid: string,
  nextFavoriteSettings: IUserEnvelopeFavoriteViewSettings
): IUserEnvelopeViewSettings => ({
  ...currentSettings,
  favoriteViewsByFilter: {
    ...(currentSettings?.favoriteViewsByFilter ?? {}),
    [favoriteFilterOguid]: nextFavoriteSettings,
  },
});

export const getEnvelopeFieldsViewSettingsNextValue = (
  currentSettings: IUserEnvelopeViewSettings | undefined,
  boxOguid: string,
  fieldsSettings: IUserEnvelopeFieldsViewSettings,
  favoriteFilterOguid?: string | null
): IUserEnvelopeViewSettings => {
  if (favoriteFilterOguid) {
    return getFavoriteViewSettingsNextValue(currentSettings, favoriteFilterOguid, {
      ...currentSettings?.favoriteViewsByFilter?.[favoriteFilterOguid],
      viewMode: getEnvelopeViewMode(currentSettings, favoriteFilterOguid, boxOguid),
      fields: fieldsSettings,
    });
  }

  return {
    ...currentSettings,
    fieldsByBox: {
      ...(currentSettings?.fieldsByBox ?? {}),
      [boxOguid]: fieldsSettings,
    },
  };
};

export const getEnvelopeFiltersViewSettingsNextValue = (
  currentSettings: IUserEnvelopeViewSettings | undefined,
  boxOguid: string,
  filtersSettings: IUserEnvelopeFiltersViewSettings,
  favoriteFilterOguid?: string | null
): IUserEnvelopeViewSettings => {
  if (favoriteFilterOguid) {
    return getFavoriteViewSettingsNextValue(currentSettings, favoriteFilterOguid, {
      ...currentSettings?.favoriteViewsByFilter?.[favoriteFilterOguid],
      viewMode: getEnvelopeViewMode(currentSettings, favoriteFilterOguid, boxOguid),
      filters: filtersSettings,
    });
  }

  return {
    ...currentSettings,
    filtersByBox: {
      ...(currentSettings?.filtersByBox ?? {}),
      [boxOguid]: filtersSettings,
    },
  };
};

export const getEnvelopeFavoriteViewSettingsNextValue = (
  currentSettings: IUserEnvelopeViewSettings | undefined,
  favoriteFilterOguid: string,
  boxOguid: string
): IUserEnvelopeViewSettings => getFavoriteViewSettingsNextValue(
  currentSettings,
  favoriteFilterOguid,
  {
    viewMode: getEnvelopeViewMode(currentSettings, null, boxOguid),
    fields: getBoxFieldsViewSettings(currentSettings, boxOguid),
    filters: getBoxFiltersViewSettings(currentSettings, boxOguid),
  }
);

export const getEnvelopeFavoriteViewSettingsRemovedValue = (
  currentSettings: IUserEnvelopeViewSettings | undefined,
  favoriteFilterOguid: string
): IUserEnvelopeViewSettings | undefined => {
  if (!currentSettings?.favoriteViewsByFilter?.[favoriteFilterOguid]) return currentSettings;

  const favoriteViewsByFilter = { ...currentSettings.favoriteViewsByFilter };

  delete favoriteViewsByFilter[favoriteFilterOguid];

  return {
    ...currentSettings,
    favoriteViewsByFilter: Object.keys(favoriteViewsByFilter).length
      ? favoriteViewsByFilter
      : undefined,
  };
};

const removeBoxSettings = <T,>(
  settingsByBox: Record<string, T> | undefined,
  boxOguid: string
): Record<string, T> | undefined => {
  if (!settingsByBox?.[boxOguid]) return settingsByBox;

  const nextSettingsByBox = { ...settingsByBox };

  delete nextSettingsByBox[boxOguid];

  return Object.keys(nextSettingsByBox).length ? nextSettingsByBox : undefined;
};

export const hasEnvelopeViewSettings = (
  settings: IUserEnvelopeViewSettings | undefined,
  boxOguid: string,
  favoriteFilterOguid?: string | null
): boolean => favoriteFilterOguid
  ? Boolean(settings?.favoriteViewsByFilter?.[favoriteFilterOguid])
  : Boolean(
      settings?.viewMode ||
      settings?.viewModesByBox?.[boxOguid] ||
      settings?.fieldsByBox?.[boxOguid] ||
      settings?.filtersByBox?.[boxOguid]
    );

export const getEnvelopeViewSettingsResetValue = (
  currentSettings: IUserEnvelopeViewSettings | undefined,
  boxOguid: string,
  favoriteFilterOguid?: string | null
): IUserEnvelopeViewSettings | undefined => {
  if (!currentSettings) return undefined;

  if (favoriteFilterOguid) {
    return getEnvelopeFavoriteViewSettingsRemovedValue(
      currentSettings,
      favoriteFilterOguid
    );
  }

  const nextSettings: IUserEnvelopeViewSettings = {
    ...currentSettings,
    viewModesByBox: removeBoxSettings(currentSettings.viewModesByBox, boxOguid),
    fieldsByBox: removeBoxSettings(currentSettings.fieldsByBox, boxOguid),
    filtersByBox: removeBoxSettings(currentSettings.filtersByBox, boxOguid),
  };

  delete nextSettings.viewMode;

  return Object.values(nextSettings).some((value) => value !== undefined)
    ? nextSettings
    : undefined;
};

export const getEnvelopeViewModeSettingsNextValue = (
  currentSettings: IUserEnvelopeViewSettings | undefined,
  viewMode: EnvelopeViewMode,
  favoriteFilterOguid?: string | null,
  boxOguid?: string
): IUserEnvelopeViewSettings => {
  if (favoriteFilterOguid) {
    return getFavoriteViewSettingsNextValue(currentSettings, favoriteFilterOguid, {
      ...currentSettings?.favoriteViewsByFilter?.[favoriteFilterOguid],
      viewMode,
    });
  }

  if (!boxOguid) {
    return {
      ...currentSettings,
      viewMode,
      cardSummaryLayout: getEnvelopeCardSummaryLayout(currentSettings),
    };
  }

  const nextSettings: IUserEnvelopeViewSettings = {
    ...currentSettings,
    viewModesByBox: {
      ...(currentSettings?.viewModesByBox ?? {}),
      [boxOguid]: viewMode,
    },
    cardSummaryLayout: getEnvelopeCardSummaryLayout(currentSettings),
  };

  delete nextSettings.viewMode;

  return nextSettings;
};

export const getEnvelopeCardSummaryLayoutSettingsNextValue = (
  currentSettings: IUserEnvelopeViewSettings | undefined,
  cardSummaryLayout: EnvelopeCardSummaryLayout
): IUserEnvelopeViewSettings => ({
  ...currentSettings,
  viewMode: getEnvelopeViewMode(currentSettings),
  cardSummaryLayout,
});
